export interface ComponentResult {
  work: number;
  power: number;
}

export interface GateBoundary {
  startTs: number;
  endTs: number;
  duration: number;
}

export interface KineticDetail extends ComponentResult {
  revSpdStart: number;
  revSpdEnd: number;
  vStart: number;
  vEnd: number;
  keStart: number;
  keEnd: number;
}

export interface GravitationalDetail extends ComponentResult {
  deltaH: number;
}

export interface RollingDetail extends ComponentResult {
  distance: number;
  revolutions: number;
}

export const GRAVITY = 9.8;

export function integrate(
  ts: number[],
  values: number[],
  startTs: number,
  endTs: number,
): number {
  if (!ts || !values || ts.length !== values.length || ts.length < 2) return 0;
  let work = 0;
  for (let i = 0; i < ts.length - 1; i++) {
    const t0 = ts[i];
    const t1 = ts[i + 1];
    if (t1 < startTs) continue;
    if (t0 > endTs) break;
    const a = Math.max(t0, startTs);
    const b = Math.min(t1, endTs);
    const dt = b - a;
    if (dt <= 0) continue;
    const span = t1 - t0;
    const v0 = span === 0 ? values[i] : values[i] + (values[i + 1] - values[i]) * ((a - t0) / span);
    const v1 = span === 0 ? values[i + 1] : values[i] + (values[i + 1] - values[i]) * ((b - t0) / span);
    work += 0.5 * (v0 + v1) * dt;
  }
  return work;
}

export function sampleAt(ts: number[], values: number[], t: number): number {
  if (!ts || !values || ts.length === 0 || ts.length !== values.length) return NaN;
  if (t <= ts[0]) return values[0];
  if (t >= ts[ts.length - 1]) return values[ts.length - 1];
  let lo = 0;
  let hi = ts.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (ts[mid] <= t) lo = mid;
    else hi = mid;
  }
  const span = ts[hi] - ts[lo];
  if (span === 0) return values[lo];
  return values[lo] + (values[hi] - values[lo]) * ((t - ts[lo]) / span);
}

export function computeRider(
  ts: number[],
  riderPower: number[],
  gate: GateBoundary,
  powerMeterSlope: number = 1,
  chainEfficiency: number = 1,
  pipelineRawPower: number = NaN,
): ComponentResult & {
  rawWork: number;
  integratedWork: number;
  pipelineRawWork: number;
  rawSource: 'pipeline' | 'integration';
} {
  const integratedWork = integrate(ts, riderPower, gate.startTs, gate.endTs);
  const pipelineRawWork = Number.isFinite(pipelineRawPower) ? pipelineRawPower * gate.duration : NaN;
  // Prefer the pipeline's `Rider_PG2 × ΔT` as the basis since it matches the data table by
  // construction. Fall back to my client-side integration if the channel isn't in the payload.
  const usePipeline = Number.isFinite(pipelineRawWork);
  const rawWork = usePipeline ? pipelineRawWork : integratedWork;
  // Pipeline (cdacalcterms.py:_calc_power):
  //   chain mode → (Rider_PG2 - ChainPower_PG3) × s  =  Rider_PG2 × ChainEff × s
  //   legacy     → Rider_PG2 × s
  const work = rawWork * chainEfficiency * powerMeterSlope;
  return {
    rawWork,
    integratedWork,
    pipelineRawWork,
    rawSource: usePipeline ? 'pipeline' : 'integration',
    work,
    power: gate.duration > 0 ? work / gate.duration : 0,
  };
}

export function computeKinetic(
  vStart: number,
  vEnd: number,
  gate: GateBoundary,
  combinedMass: number,
  wheelCircumference: number,
  pipelinePower: number = NaN,
): KineticDetail & { computedWork: number; rawSource: 'pipeline' | 'computed' } {
  // Pipeline convention (cda_calculator.py:504-510):
  //   spd_0 = END, spd_1 = START
  //   kinetic = -0.5 × m × (END² − START²) = KE_start − KE_end
  //   "All retarding forces should be negative, propelling positive" (line 515)
  // i.e. accelerating (KE_end > KE_start) → kinetic NEGATIVE; decelerating → POSITIVE.
  const revSpdStart = wheelCircumference > 0 ? vStart / wheelCircumference : NaN;
  const revSpdEnd = wheelCircumference > 0 ? vEnd / wheelCircumference : NaN;
  const keStart = 0.5 * combinedMass * vStart * vStart;
  const keEnd = 0.5 * combinedMass * vEnd * vEnd;
  const computedWork = keStart - keEnd;
  // Prefer pipeline's `Kinetic_PG3 × ΔT` when available (matches the data table by
  // construction; my v_start/v_end can drift a few % if the precomputed channels were
  // stored under a different wheel circumference).
  const usePipeline = Number.isFinite(pipelinePower);
  const work = usePipeline ? pipelinePower * gate.duration : computedWork;
  return {
    work,
    computedWork,
    rawSource: usePipeline ? 'pipeline' : 'computed',
    power: gate.duration > 0 ? work / gate.duration : 0,
    revSpdStart,
    revSpdEnd,
    vStart,
    vEnd,
    keStart,
    keEnd,
  };
}

export function computeGravitational(
  deltaH: number,
  gate: GateBoundary,
  combinedMass: number,
  gravity: number = GRAVITY,
): GravitationalDetail {
  // Pipeline convention (cdacalcterms.py): work = -1 × m × g × Δh, so climbing → negative
  // (rider spends energy), descending → positive (rider gains energy from gravity).
  const work = -1 * combinedMass * gravity * deltaH;
  return {
    work,
    power: gate.duration > 0 ? work / gate.duration : 0,
    deltaH,
  };
}

export function computeRolling(
  distance: number,
  gate: GateBoundary,
  combinedMass: number,
  crr: number,
  wheelCircumference: number,
  gravity: number = GRAVITY,
): RollingDetail {
  // distance comes from precomputed `WhlLdistDiff_sG3` (= WhlRrevDiff_NG3 × C) and `crr` is
  // the temperature-corrected `CrrInput_xG5` per traverse — both match the pipeline.
  const revolutions = wheelCircumference > 0 ? distance / wheelCircumference : 0;
  const work = -1 * distance * combinedMass * gravity * crr;
  return {
    work,
    power: gate.duration > 0 ? work / gate.duration : 0,
    distance,
    revolutions,
  };
}

export function computeAlpha(
  ts: number[],
  dynPres: number[],
  whlRevSpd: number[],
  gate: GateBoundary,
  cpCorr: number,
  wheelCircumference: number,
): ComponentResult {
  if (!dynPres || !whlRevSpd || dynPres.length !== whlRevSpd.length) {
    return { work: 0, power: 0 };
  }
  const alphaPower = dynPres.map((p, i) => p * cpCorr * (whlRevSpd[i] * wheelCircumference));
  const work = integrate(ts, alphaPower, gate.startTs, gate.endTs);
  return { work, power: gate.duration > 0 ? work / gate.duration : 0 };
}

export interface AlphaBaseDetail extends ComponentResult {
  alphaBase: number;       // ∫ q_raw × cos(yaw) × rev_speed dt over the gate (no cp_corr, no C)
  alphaBasePower: number;  // alphaBase / duration
  alphaWork: number;       // alphaBase × cp_corr × wheel_circumference
  alphaPower: number;      // alphaWork / duration
}

/**
 * AlphaBase derivation from the three raw gridded inputs. Mirrors the pipeline's
 * gridded compute in cda_post_processing.py:208-210:
 *
 *   AlphaBase = ∫ DynPres_pG21 × cos(AirYaw_AG31) × WhlRspd_n dt
 *   Alpha     = AlphaBase × cp_corr × wheel_circumference
 */
export function computeAlphaBase(
  ts: number[],
  dynPresRaw: number[],
  airYaw: number[],
  whlRevSpd: number[],
  gate: GateBoundary,
  cpCorr: number,
  wheelCircumference: number,
): AlphaBaseDetail {
  if (
    !dynPresRaw || !airYaw || !whlRevSpd
    || dynPresRaw.length !== whlRevSpd.length
    || dynPresRaw.length !== airYaw.length
  ) {
    return {
      work: 0, power: 0,
      alphaBase: 0, alphaBasePower: 0,
      alphaWork: 0, alphaPower: 0,
    };
  }
  const integrand = dynPresRaw.map((p, i) => {
    const yawDeg = airYaw[i];
    if (!Number.isFinite(yawDeg) || !Number.isFinite(p) || !Number.isFinite(whlRevSpd[i])) return 0;
    return p * Math.cos((yawDeg * Math.PI) / 180) * whlRevSpd[i];
  });
  const alphaBase = integrate(ts, integrand, gate.startTs, gate.endTs);
  const alphaWork = alphaBase * cpCorr * wheelCircumference;
  const dur = gate.duration;
  return {
    work: alphaWork,
    power: dur > 0 ? alphaWork / dur : 0,
    alphaBase,
    alphaBasePower: dur > 0 ? alphaBase / dur : 0,
    alphaWork,
    alphaPower: dur > 0 ? alphaWork / dur : 0,
  };
}

export function computeElevationDelta(
  ts: number[],
  beta: number[],
  startTs: number,
  endTs: number,
  gravity: number = GRAVITY,
): number {
  if (!ts || !beta || ts.length === 0 || ts.length !== beta.length) return 0;
  const betaStart = sampleAt(ts, beta, startTs);
  const betaEnd = sampleAt(ts, beta, endTs);
  if (!Number.isFinite(betaStart) || !Number.isFinite(betaEnd)) return 0;
  return (betaEnd - betaStart) / -gravity;
}

export interface RunConditions {
  airspeed: number | null;
  yaw: number | null;
  chest: number | null;
  head: number | null;
}

export interface NormalisationRefs {
  speedRef: number;
  angleRef: number;
  chestRef: number;
  headRef: number;
}

export interface NormalisationSlopes {
  speedSlope: number;
  xwSlope: number;
  chestSlope: number;
  headSlope: number;
}

export interface ReverseNormalisationResult {
  airspeedAdj: number;
  yawAdj: number;
  chestAdj: number;
  headAdj: number;
  cdaAdjusted: number;
}

// Forward formulas applied with run-level inputs. Same shape as the per-traverse
// adjustments in NormalisationSummary: linear in airspeed/chest/head, quadratic
// in yaw. Returns NaN for cdaAdjusted when cdaRef is missing.
export function computeReverseNormalisation(
  cdaRef: number,
  conditions: RunConditions,
  refs: NormalisationRefs,
  slopes: NormalisationSlopes,
): ReverseNormalisationResult {
  const airspeedAdj =
    conditions.airspeed != null && Number.isFinite(conditions.airspeed) && slopes.speedSlope !== 0
      ? (conditions.airspeed - refs.speedRef) * slopes.speedSlope
      : 0;
  const yawAdj =
    conditions.yaw != null && Number.isFinite(conditions.yaw) && slopes.xwSlope !== 0
      ? (conditions.yaw - refs.angleRef) ** 2 * slopes.xwSlope
      : 0;
  const chestAdj =
    conditions.chest != null && Number.isFinite(conditions.chest) && slopes.chestSlope !== 0
      ? (conditions.chest - refs.chestRef) * slopes.chestSlope
      : 0;
  const headAdj =
    conditions.head != null && Number.isFinite(conditions.head) && slopes.headSlope !== 0
      ? (conditions.head - refs.headRef) * slopes.headSlope
      : 0;
  const cdaAdjusted = Number.isFinite(cdaRef)
    ? cdaRef + airspeedAdj + yawAdj + chestAdj + headAdj
    : NaN;
  return { airspeedAdj, yawAdj, chestAdj, headAdj, cdaAdjusted };
}

